// Reference RichTextAdapter backed by `react-simple-wysiwyg` (a contentEditable
// + execCommand editor that emits HTML directly). Shipped as a SEPARATE entry
// (`@scientist/dynamic-form-renderer/richtext-simple`) so apps that don't use
// rich text never pull the editor into the core bundle. `react-simple-wysiwyg`
// is an optionalDependency; a host wires this adapter via the renderer's
// `richTextAdapter` prop.
//
// The toolbar mirrors the legacy alpaca `wysiwyg.js` full toolbar intent:
// undo/redo, block styles, bold/italic/underline/strikethrough, lists,
// justification, link, clear formatting.
//
// Icons are Lucide SVGs, RX's icon set since Font Awesome was removed (its
// icon tags left these buttons blank). The lib's built-in Btn* components
// render fixed unicode content that can't be overridden via props, so each
// button is recreated with `createButton` using the built-ins' exact titles
// and commands.

import {
  Bold,
  Italic,
  Link,
  List,
  ListOrdered,
  Redo2,
  RemoveFormatting,
  Strikethrough,
  TextAlignCenter,
  TextAlignEnd,
  TextAlignStart,
  Underline,
  Undo2,
  type LucideIcon,
} from 'lucide-react'
import {
  Editor,
  EditorProvider,
  Separator,
  Toolbar,
  createButton,
  createDropdown,
  type EditorState,
} from 'react-simple-wysiwyg'
import type { RichTextEditorProps } from '../rendererContext'
import { sanitizeRichText } from '../richText'

function icon(Icon: LucideIcon) {
  return <Icon size={16} strokeWidth={1.75} aria-hidden />
}

// Visual overrides on top of the lib's injected `.rsw-*` defaults. Every value
// is a `--rsw-*` custom property with a fallback matching the legacy look, so
// hosts restyle the editor from their own stylesheet (e.g. RX's storefront
// tokens). Appended to <head> after the lib's rules, so these win by order.
const STYLE_OVERRIDE_ID = 'rsw-dynamic-form-overrides'
const STYLE_OVERRIDES = `
.rsw-editor {
  border-radius: var(--rsw-border-radius, 0);
  border-color: var(--rsw-border-color, rgb(204, 204, 204));
}
.rsw-editor:has(.rsw-ce[aria-invalid="true"]) {
  border-color: var(--rsw-invalid-border-color, var(--bs-form-invalid-border-color, #dc3545));
}
.rsw-toolbar {
  background-color: var(--rsw-toolbar-bg, #fff);
  border-bottom-color: var(--rsw-border-color, rgb(204, 204, 204));
}
.rsw-separator {
  border-right-color: var(--rsw-border-color, rgb(204, 204, 204));
}
.rsw-btn {
  font-size: var(--rsw-btn-font-size, 1em);
}
.rsw-dd {
  background-color: transparent;
  border: none;
  width: auto;
  padding: 0 0.25em;
  font-size: var(--rsw-btn-font-size, 1em);
}
`

function ensureStyleOverrides() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_OVERRIDE_ID)) return
  const style = document.createElement('style')
  style.id = STYLE_OVERRIDE_ID
  style.textContent = STYLE_OVERRIDES
  document.head.appendChild(style)
}

// Block styles limited to what the sanitize allowlist keeps (`p`, `h1`-`h3` are
// all in RICH_TEXT_ALLOWED_TAGS). The package's stock BtnStyles offers
// Normal(DIV)/H1/Code, which doesn't match the forms' needs.
const BtnStyles = createDropdown('Styles', [
  ['Paragraph', 'formatBlock', 'P'],
  ['Heading 1', 'formatBlock', 'H1'],
  ['Heading 2', 'formatBlock', 'H2'],
  ['Heading 3', 'formatBlock', 'H3'],
])

// Force `styleWithCSS` around justify commands: with it off (the default)
// Firefox emits `align="..."` attributes, which BOTH sanitize layers strip
// (`align` is not in RICH_TEXT_ALLOWED_ATTR nor the server allowlist). With it
// on, alignment lands in the allowed `style` attribute as `text-align`.
function justifyCommand(command: string) {
  return () => {
    document.execCommand('styleWithCSS', false, 'true')
    document.execCommand(command)
    document.execCommand('styleWithCSS', false, 'false')
  }
}

// Same behavior as the lib's built-in BtnLink, with a Lucide glyph.
function linkCommand({ $selection }: EditorState) {
  if ($selection?.nodeName === 'A') {
    document.execCommand('unlink')
  } else {
    // eslint-disable-next-line no-alert
    document.execCommand('createLink', false, prompt('URL', '') || undefined)
  }
}

const BtnUndo = createButton('Undo', icon(Undo2), 'undo')
const BtnRedo = createButton('Redo', icon(Redo2), 'redo')
const BtnBold = createButton('Bold', icon(Bold), 'bold')
const BtnItalic = createButton('Italic', icon(Italic), 'italic')
const BtnUnderline = createButton('Underline', icon(Underline), 'underline')
const BtnStrikeThrough = createButton('Strike through', icon(Strikethrough), 'strikeThrough')
const BtnBulletList = createButton('Bullet list', icon(List), 'insertUnorderedList')
const BtnNumberedList = createButton('Numbered list', icon(ListOrdered), 'insertOrderedList')
const BtnJustifyLeft = createButton('Justify left', icon(TextAlignStart), justifyCommand('justifyLeft'))
const BtnJustifyCenter = createButton('Justify center', icon(TextAlignCenter), justifyCommand('justifyCenter'))
const BtnJustifyRight = createButton('Justify right', icon(TextAlignEnd), justifyCommand('justifyRight'))
const BtnLink = createButton('Link', icon(Link), linkCommand)
const BtnClearFormatting = createButton('Clear formatting', icon(RemoveFormatting), 'removeFormat')

export function SimpleWysiwygRichText({
  id,
  value,
  disabled,
  placeholder,
  ariaInvalid,
  ariaRequired,
  ariaDescribedBy,
  ariaLabelledBy,
  onChange,
  onBlur,
}: RichTextEditorProps) {
  ensureStyleOverrides()
  return (
    <EditorProvider>
      <Editor
        id={id}
        value={value}
        disabled={disabled}
        placeholder={placeholder}
        // The editable area is a `div`: the role and the label reference make it
        // a named, multi-line textbox to assistive tech.
        role="textbox"
        aria-multiline
        aria-labelledby={ariaLabelledBy}
        aria-invalid={ariaInvalid}
        aria-required={ariaRequired}
        aria-describedby={ariaDescribedBy}
        // The adapter is contractually responsible for emitting sanitized HTML.
        onChange={(event) => onChange(sanitizeRichText(event.target.value))}
        onBlur={onBlur}
      >
        <Toolbar>
          <BtnUndo />
          <BtnRedo />
          <Separator />
          <BtnStyles />
          <Separator />
          <BtnBold />
          <BtnItalic />
          <BtnUnderline />
          <BtnStrikeThrough />
          <Separator />
          <BtnBulletList />
          <BtnNumberedList />
          <Separator />
          <BtnJustifyLeft />
          <BtnJustifyCenter />
          <BtnJustifyRight />
          <Separator />
          <BtnLink />
          <BtnClearFormatting />
        </Toolbar>
      </Editor>
    </EditorProvider>
  )
}
